import { DateTime } from "luxon";
import type * as am4charts from "@amcharts/amcharts4/charts";
import type { TimeUnit, FontWeight } from "@amcharts/amcharts4/core";

export const trendsFormat = "yyyy-MM-dd";
export const resultsSinceFormat = "MMM dd, yyyy";

export interface OneSeriesTrendProperty {
  title: string;
  queryNameVariable: string[];
  legend: string;
}

export const createValueAxis = (
  valueAxis: am4charts.ValueAxis,
  name: string,
  max?: number
): void => {
  valueAxis.strictMinMax = true;
  // Preserve runtime behavior: clearing max (null) when max is 0; amCharts allows null at runtime.
  valueAxis.max = max === 0 ? (null as unknown as number) : (max as number);
  valueAxis.min = 0;
  valueAxis.renderer.labels.template.fontSize = 12;
  valueAxis.renderer.grid.template.location = 0;
  valueAxis.paddingLeft = 10;
  valueAxis.paddingRight = 20;
  valueAxis.layout = "absolute";
  valueAxis.title.text = name;
  valueAxis.title.fontSize = 12;
  valueAxis.title.fontWeight = 500 as unknown as FontWeight;
  valueAxis.title.rotation = 0;
  valueAxis.title.dy = -30;
  valueAxis.title.align = "center";
  valueAxis.title.valign = "top";
  valueAxis.tooltip!.dx = 50;
};

export const createDateAxis = (
  dateAxis: am4charts.DateAxis,
  timeScale: string
): void => {
  // DateAxis dataFields has no `category` key; original JS set it regardless
  // (no-op at runtime). Cast to a wider record to preserve the assignment.
  (dateAxis.dataFields as Record<string, string>).category = "date";
  dateAxis.renderer.grid.template.location = 0;
  dateAxis.renderer.grid.template.strokeOpacity = 0;
  dateAxis.renderer.labels.template.fontSize = 12;
  dateAxis.baseInterval = {
    timeUnit: dateFormatKey[timeScale] as TimeUnit,
    count: timeScale === "QUARTER" ? 3 : 1,
  };

  dateAxis.dateFormats.setKey(
    dateFormatKey[timeScale] as TimeUnit,
    timeScaleFormatter[timeScale]
  );

  dateAxis.markUnitChange = false;

  dateAxis.tooltipDateFormat = timeScaleFormatter[timeScale];
};

const dateFormatKey: Record<string, string> = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  QUARTER: "month",
  // YEAR: "year",
};

export const timeScaleFormatter: Record<string, string> = {
  DAY: "MMM dd, yyyy",
  WEEK: "MMM dd, yyyy",
  MONTH: "MMM yyyy",
  QUARTER: "q.yyyy",
  // YEAR: "yyyy",
};

export const timeScalePeriodChangeFormatter: Record<string, string> = {
  DAY: "MMM dd, yyyy",
  WEEK: "MMM dd, yyyy",
  MONTH: "MMM yyyy",
  QUARTER: "q.yyyy",
  YEAR: "yyyy",
};

export const oneSeriesTrendProperties: Record<string, OneSeriesTrendProperty> =
  {
    newFindingsByDiscoveryDate: {
      title: "New Findings By Discovery Date",
      queryNameVariable: ["new"],
      legend: "New Findings",
    },
    openFindingsByDiscoveryDate: {
      title: "Open Findings By Discovery Date",
      queryNameVariable: ["open"],
      legend: "Open Findings",
    },
  };

export const getSinceText = (timeScale: string): string => {
  switch (timeScale) {
    case "DAY": {
      return DateTime.now().minus({ days: 12 }).toFormat(resultsSinceFormat);
    }
    case "WEEK": {
      return DateTime.now().minus({ weeks: 12 }).toFormat(resultsSinceFormat);
    }
    case "MONTH": {
      return DateTime.now().minus({ months: 12 }).toFormat(resultsSinceFormat);
    }
    default: {
      return "";
    }
  }
};

export const getStartDate = (timeScale: string): string => {
  switch (timeScale) {
    case "DAY": {
      return DateTime.now().minus({ days: 11 }).toFormat(trendsFormat);
    }
    case "WEEK": {
      return DateTime.now().minus({ weeks: 12 }).toFormat(trendsFormat);
    }
    case "MONTH": {
      return DateTime.now().minus({ months: 12 }).toFormat(trendsFormat);
    }
    case "QUARTER": {
      return "2000-01-01";
    }
    case "YEAR": {
      return "2000-01-01";
    }
    default: {
      return "2000-01-01";
    }
  }
};
